import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CalcConfigService } from '_services/calc-config.service';
import { Expense, ExpenseRule, ExpenseNameMap } from '../../_models/expense';

import { find, isEqual } from 'lodash-es';
import { HelpComponent } from '../../_shared/help/help.component';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';
import { MatCard } from '@angular/material/card';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';

@Component({
    selector: 'app-costs',
    templateUrl: './costs.component.html',
    styleUrls: ['./costs.component.css'],
    imports: [HelpComponent, MatButton, AidYearComponent, MatCard, RouterLink, MatRadioGroup, ReactiveFormsModule, FormsModule, MatRadioButton, MatFormField, MatLabel, MatSelect, MatOption, MatInput, AsyncPipe, DecimalPipe]
})
export class CostsComponent implements OnInit, OnDestroy {
  editMode = false;
  schoolId = '';
  costsCopy?: Expense[];
  expenseNameMap = ExpenseNameMap;
  private readonly expenseHeaderPrefix = 'costs-expense-';

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    public cc: CalcConfigService
  ) {
    this.titleService.setTitle('Costs' + ' | NPC Admin');
  }

  ngOnInit() {
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
  }

  setMode(mode: 'edit' | 'save' | 'cancel') {
    switch (mode) {
      case 'edit':
        this.costsCopy = JSON.parse(JSON.stringify(this.cc.expenses.value)); // lodash has a deep clone function
        this.editMode = true;
        break;
      case 'cancel':
        this.cc.expenses.next(this.costsCopy);
        this.editMode = false;
        break;
      case 'save':
        {
          const expenses = this.cc.expenses.value;
          expenses.forEach(exp => {
            exp.rules.forEach(rule => {
              // If tableId is 1 set it to null
              rule.tableId = (rule.tableId === '1' || !rule.tableId) ? null : rule.tableId;
              // If saving a rule with a table, zero out it's amount
              rule.amount = rule.tableId ? 0 : rule.amount;
            });
          });
          // this.addMissingExpense(expenses);
          this.cc.expenses.next(expenses);
          if (!isEqual(this.costsCopy, this.cc.expenses.value)) {
            this.cc.saveExpenses(this.schoolId, this.cc.expenses.value);
          }
          this.editMode = false;
          break;
        }
    }
  }

  // Unused code:  Used to add a missing expense to the list of expenses
  private addMissingExpense(expenses: Expense[]) {
    const travelExpense: Expense = {
      name: 'Transportation',
      order: 4,
      rules: [
        {
          populationId: '1',
          amount: 0
        }
      ],
      type: 'Transportation'
    };
    expenses.push(travelExpense);
  }

  addRule(exp: Expense) {
    const aRule: ExpenseRule = {
      populationId: '1',
      amount: 0
    };
    exp.rules.push(aRule);
  }

  deleteRule(exp: Expense, index: number) {
    if (exp.rules.length > 1) {
      exp.rules.splice(index, 1);
    } else {
      alert('Cannot delete last rule for ' + exp.name);
    }
  }

  setRulePopulation(r: ExpenseRule) {
    r.population = r.populationId === '1' ? null : find(this.cc.populations.value, { 'id': r.populationId });
  }

  setRuleTable(r: ExpenseRule) {
    r.table = r.tableId ? this.cc.getTable(r.tableId) : null;
  }

  getPopulationName(popId: string): string {
    const pop = popId === '1' ? null : find(this.cc.populations.value, { 'id': popId });
    if (pop) {
      return pop.name;
    } else {
      return 'All';
    }
  }
  getTableName(tableId: string): string {
    const table = find(this.cc.tables.value, { 'tableId': tableId });
    if (table) {
      return table.name;
    } else {
      return '';
    }
  }

  getExpenseHeaderId(expense: Expense): string {
    const raw = expense.type ? expense.type.toLowerCase() : 'item';
    const sanitized = raw.replace(/[^a-z0-9]+/g, '-');
    return `${this.expenseHeaderPrefix}${sanitized}`;
  }

  getFieldLabelIds(field: 'population' | 'amount' | 'table', expense: Expense): string {
    const columnHeaderId = field === 'population' ? 'costs-population-header' : 'costs-amount-header';
    const rowHeaderId = this.getExpenseHeaderId(expense);
    return `${columnHeaderId} ${rowHeaderId}`;
  }

  ngOnDestroy() {
    if (this.editMode) {
      this.setMode('cancel');
    }
  }
}
